import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import { format } from "date-fns";
import { useNavigation } from "@react-navigation/native";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { Button, Input } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";
import {
  ExerciseDto,
  ExerciseService,
  EnumerationItem,
} from "../../../services/ExerciseService";
import { useAuth } from "../../../../context/AuthContext";
import { getMinutesValidationError, parseValidMinutes } from "../../../shared/utils/minutesValidation";

export function ExerciseScreen() {
  const navigation = useNavigation<any>();
  const { user } = useAuth();

  const todayStr = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  const [date, setDate] = useState(todayStr);

  const [duration, setDuration] = useState(""); // minutes only
  const [exerciseTypeId, setExerciseTypeId] = useState("");
  const [otherType, setOtherType] = useState("");

  const [types, setTypes] = useState<EnumerationItem[]>([]);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const durationError = useMemo(() => getMinutesValidationError(duration), [duration]);

  /* ---------------- LOAD TYPES ---------------- */

  useEffect(() => {
    loadTypes();
  }, []);

  const loadTypes = async () => {
    const res = await ExerciseService.getExerciseTypes("Exercise");

    if (res?.status === 200) {
      setTypes(res.data);

      const other = res.data.find((x: { value: string; }) => x.value === "Other");

      if (other) {
        setExerciseTypeId(other.enumerationId);
      }
    }
  };

  const resetForm = () => {
    setDuration("");
    setOtherType("");

    const other = types.find((x) => x.value === "Other");

    if (other) {
      setExerciseTypeId(other.enumerationId);
    }
  };

  /* ---------------- DATE PICKER ---------------- */

  const openDatePicker = () => {
    DateTimePickerAndroid.open({
      value: new Date(date),
      mode: "date",
      maximumDate: new Date(),
      display: "calendar",
      onChange: (event, selectedDate) => {
        if (event.type === "set" && selectedDate) {
          const newDate = format(selectedDate, "yyyy-MM-dd");
          setDate(newDate);
          resetForm();
        }
      },
    });
  };


  /* ---------------- FORMAT MINUTES TO HH:mm:ss ---------------- */

  const formatDuration = (minutesInput: string) => {
    const totalMinutes = parseValidMinutes(minutesInput);
    if (totalMinutes == null) return null;

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:00`;
  };

  /* ---------------- SAVE ---------------- */

  const onSave = async () => {
    if (!user?.userId) return Flash.error("User not found");
    if (!duration.trim()) return Flash.error("Enter duration in minutes");
    if (!exerciseTypeId) return Flash.error("Select exercise type");
    if (durationError) return Flash.error(durationError);

    const formattedDuration = formatDuration(duration);

    if (!formattedDuration) {
      Flash.error("Enter valid minutes");
      return;
    }

    try {
      setLoading(true);

      const selected = types.find(
        (t) => t.enumerationId === exerciseTypeId
      );

      const payload: ExerciseDto = {
        exerciseId: null,
        userId: user.userId,
        date,
        exerciseTypeId,
        otherType:
          selected?.value === "Other"
            ? otherType?.trim() || null
            : null,
        duration: formattedDuration,
      };

      console.log("Payload:", payload);

      const res = await ExerciseService.create(payload);

      if (res?.status !== 200) {
        Flash.error("Failed to save");
        return;
      }

      Flash.success("Success!", "Exercise Saved Successfully!");
      resetForm();
      loadTypes();
      navigation.navigate("ExerciseHistory");
    } catch (error: any) {
      console.log("API ERROR:", error?.response?.data);
      Flash.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <DivineBackground overlayOpacity={0.92}>
      <View style={{ flex: 1 }}>
        <Header title="Exercise" showBack showProfile />

        <ScrollView contentContainerStyle={styles.container}>
          <View style={styles.card}>
            {/* DATE + HISTORY */}
            <View style={styles.topRow}>
              <Pressable onPress={openDatePicker} style={styles.dateChip}>
                <Text style={styles.dateChipIcon}>📅</Text>
                <Text style={styles.dateChipText}>
                  {format(new Date(date), "dd-MM-yyyy")}
                </Text>
              </Pressable>

              <Pressable
                onPress={() => navigation.navigate("ExerciseHistory")}
                style={styles.historyBtn}
              >
                <Text style={styles.historyIcon}>🕘</Text>
                <Text style={styles.historyText}>History</Text>
              </Pressable>
            </View>

            {/* FORM */}
            <View style={{ marginTop: 16, gap:10 }}>
              <Input
                label="Duration (Minutes)"
                placeholder="Enter minutes (e.g. 30)"
                keyboardType="numeric"
                value={duration}
                onChangeText={(text) => {
                  const numeric = text.replace(/[^0-9]/g, "");
                  setDuration(numeric);
                }}
              />
              {durationError ? <Text style={styles.validationText}>{durationError}</Text> : null}

              {/* <Text style={styles.label}>Exercise Program</Text>

              <View style={styles.selectBox}>
                <Text
                  style={[
                    styles.selectText,
                    !exerciseTypeId && { color: "#9ca3af" },
                  ]}
                >
                  {types.find(
                    (t) => t.enumerationId === exerciseTypeId
                  )?.value || "Select Exercise Type"}
                </Text>
              </View> */}

              {types.find(
                (t) => t.enumerationId === exerciseTypeId
              )?.value === "Other" && (
                  <Input 
                    label="Custom Exercise Type"
                    placeholder="Enter exercise name"
                    value={otherType}
                    onChangeText={setOtherType}
                  />
                )}
            </View>

            {/* BUTTONS */}
            <View style={styles.btnRow}>
              <Pressable
                style={styles.cancelBtn}
                onPress={() => navigation.goBack()}
              >
                <Text style={styles.cancelText}>Cancel</Text>
              </Pressable>

              <View style={{ flex: 1 }}>
                <Button
                  title="Save"
                  onPress={onSave}
                  loading={loading}
                  disabled={loading || !!durationError}
                />
              </View>
            </View>

            {loading && (
              <ActivityIndicator style={{ marginTop: 12 }} />
            )}
          </View>
        </ScrollView>

        {/* RADIO DROPDOWN MODAL */}
        <Modal visible={dropdownVisible} transparent animationType="fade">
          <Pressable
            style={styles.modalOverlay}
            onPress={() => setDropdownVisible(false)}
          >
            <View style={styles.dropdownCard}>
              <ScrollView>
                {types.map((item) => {
                  const selected =
                    item.enumerationId === exerciseTypeId;

                  return (
                    <Pressable
                      key={item.enumerationId}
                      style={styles.radioRow}
                      onPress={() => {
                        setExerciseTypeId(item.enumerationId);
                        setDropdownVisible(false);
                        if (item.value !== "Other")
                          setOtherType("");
                      }}
                    >
                      <View
                        style={[
                          styles.radioOuter,
                          selected && styles.radioOuterActive,
                        ]}
                      >
                        {selected && (
                          <View style={styles.radioInner} />
                        )}
                      </View>
                      <Text>{item.value}</Text>
                    </Pressable>
                  );
                })}
              </ScrollView>
            </View>
          </Pressable>
        </Modal>
      </View>
    </DivineBackground>
  );
}

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: {
    padding: 14,
    paddingBottom: 30,
  },

  card: {
    borderRadius: 16,
    padding: 14,
    backgroundColor: "rgba(255,255,255,0.88)",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
    shadowColor: "#000",
    shadowOpacity: 0.12,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
    elevation: 4,
  },

  topRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },

  dateChip: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(229,247,238,0.85)",
    borderWidth: 1,
    borderColor: "rgba(16,185,129,0.28)",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 999,
  },

  dateChipIcon: {
    fontSize: 14,
    marginRight: 6,
  },

  dateChipText: {
    fontSize: 13,
    fontWeight: "800",
    color: "#1a4731",
  },

  historyBtn: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    height: 34,
    borderRadius: 10,
    backgroundColor: "rgba(255,255,255,0.55)",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
  },

  historyIcon: {
    fontSize: 14,
    marginRight: 6,
  },

  historyText: {
    fontSize: 12,
    fontWeight: "800",
    color: "rgba(26,71,49,0.78)",
  },

  customType: {
    marginTop: 10
  },

  label: {
    marginTop: 14,
    marginBottom: 6,
    fontWeight: "800",
    fontSize: 13,
    color: "#88706c",
  },

  selectBox: {
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.12)",
    borderRadius: 12,
    padding: 14,
    backgroundColor: "white",
    marginBottom: 10
  },

  selectText: {
    fontSize: 14,
  },
  validationText: {
    marginTop: -6,
    fontSize: 11,
    fontWeight: "800",
    color: "#DC2626",
  },

  btnRow: {
    flexDirection: "row",
    gap: 10,
    marginTop: 20,
  },

  cancelBtn: {
    flex: 1,
    height: 46,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.12)",
    backgroundColor: "rgba(255,255,255,0.9)",
    alignItems: "center",
    justifyContent: "center",
  },

  cancelText: {
    fontSize: 14,
    fontWeight: "900",
    color: "rgba(26,71,49,0.60)",
  },

  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.35)",
    justifyContent: "center",
    padding: 20,
  },

  dropdownCard: {
    backgroundColor: "white",
    borderRadius: 18,
    maxHeight: 420,
  },

  radioRow: {
    flexDirection: "row",
    alignItems: "center",
    padding: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
  },

  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: "#d1d5db",
    marginRight: 12,
  },

  radioOuterActive: {
    borderColor: "#16a34a",
  },

  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: "#16a34a",
    alignSelf: "center",
    marginTop: 3,
  },
});
